import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import useLocale from '../../i18n/useLocale';
import { TaxRate, TaxRateSearchResponse } from 'axiosApi/models';

export type invoiceProviderContextProps = {
  taxesValues?: Array<TaxRate>;
  setTaxesValues?: Function;
  invoiceTypes?: Array<any>;
  isLoading?: boolean;
  currencies?: Array<any>;
  setCurrencies?: Function;
  isManualCreation?: boolean;
  billToPersonas?: Array<any>;
  billFromPersonas?: Array<any>;
  isBillFromPersonasLoading?: boolean;
  isBillToPersonasLoading?: boolean;
  input?: any;
  setInput?: any;
  loadBillingAddressByPersonaId?: Function;
  isBillingAddressFromLoading?: boolean;
  isBillingAddressToLoading?: boolean;
  errors?: any;
  setErrors?: Function;
};

const DefaultInvoiceInput = {
  to: '',
  Amount: 1,
  number: '',
  from: '',
  idFrom: '',
  idTo: '',
  fileURI: '',
  idWorkflowState: null,
  paymentTerms: '',
  idInvoiceType: '',
  idCurrency: '',
  comments: '',
  unit: '',
  invoiceTaxes: [],
  invoiceItems: [
    {
      id: 1,
      amount: '',
      unit: 1,
      invoiceItemTaxes: [],
    },
  ],
};

const InvoiceContext = createContext<invoiceProviderContextProps>({});

export const InvoiceProvider = ({ children, review = false, view = false, edit = false }) => {
  const [taxesValues, setTaxesValues] = useState<any>();
  const [countyId, setCountryId] = useState<any>(null);
  const [isCountryLoading, setIsCountryLoading] = useState<boolean>();
  const [invoiceTypes, setInvoiceTypes] = useState<any>();
  const [isInvoiceTypeLoading, setIsInvoiceTypeLoading] = useState<boolean>();
  const [currencies, setCurrencies] = useState<any>();
  const [isCurrencyLoading, setIsCurrencyLoading] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [isBillFromPersonasLoading, setIsBillFromPersonasLoading] = useState<boolean>();
  const [isBillToPersonasLoading, setIsBillToPersonasLoading] = useState<boolean>();
  const [isBillingAddressToLoading, setIsBillingAddressToLoading] = useState<boolean>();
  const [isBillingAddressFromLoading, setIsBillingAddressFromLoading] = useState<boolean>();
  const [billToPersonas, setBillToPersonas] = useState<any>();
  const [billFromPersonas, setBillFromPersonas] = useState<any>();
  const [input, setInput] = useState<any>(DefaultInvoiceInput);
  const [isManualCreation, setIsManualCreation] = useState<boolean>(true);
  const [errors, setErrors] = useState<any>();
  const invoiceLoadedRef = useRef(false);
  const api: Api = useApi();
  const { locale } = useLocale();
  const id = window.location.pathname.split('/').pop();

  useEffect(() => {
    if (review && !isCurrencyLoading && !isInvoiceTypeLoading) {
      // Only load the invoice once — subsequent triggers come from country/tax reloads
      // after the user changes idFrom, and must not overwrite the form.
      if (invoiceLoadedRef.current) return;
      invoiceLoadedRef.current = true;
      setIsManualCreation(false);
      setIsLoading(true);
      api.invoiceApi
        .apiVversionInvoiceByJobIdJobIdGet(id, '1')
        .then((response) => {
          const responseData = response.data?.['data'] as any;
          const [year, month, day] = responseData?.date?.split('T')[0].split('-');
          const date = new Date(year, month - 1, day);
          responseData.invoiceItems = responseData.invoiceItems.map((item, index) => ({
            ...item,
            id: index + 1,
            rate: item.amount,
            invoiceItemTaxes: (item.invoiceItemTaxes ?? []).map((tax) => ({
              id: tax.idTaxRate,
              rate: tax.taxRate,
              amount: tax.taxAmount,
            })),
          }));
          responseData.invoiceTaxes = responseData.invoiceTaxes.map((tax, index) =>
            tax.idTaxRate === 0 ? { ...tax, idTaxRate: index + 10000 } : tax
          );
          setInput((prevInput) => ({
            ...prevInput,
            ...responseData,
            idCurrency:
              currencies?.find((currency) => currency.id === responseData.currency.id)?.id || '',
            date: date,
          }));

          if (responseData.confidence) {
            const newErrors = {};
            const confidenceDictionary = responseData.confidence;

            const relevantKeys = [
              'addressFrom',
              'addressTo',
              'currency',
              'date',
              'idCurrency',
              'idInvoiceType',
              'number',
              'personaFrom',
              'personaTo',
              'to',
              'workflowState',
              'idFrom',
              'idTo',
            ];

            relevantKeys.forEach((key) => {
              if (!confidenceDictionary[key]) {
                newErrors[key] = 'missing confidence';
              } else if (confidenceDictionary[key] < 100) {
                newErrors[key] = 'low confidence';
              }
            });

            if (responseData.invoiceItems) {
              responseData.invoiceItems.forEach((item, index) => {
                const relevantKeys = ['rate', 'quantity', 'description'];
                relevantKeys.forEach((key) => {
                  if (!confidenceDictionary[`invoiceItems[${index}].${key}`]) {
                    newErrors[`invoiceItems[${index + 1}].${key}`] = 'missing confidence';
                  } else if (confidenceDictionary[`invoiceItems[${index}].${key}`] < 100) {
                    newErrors[`invoiceItems[${index + 1}].${key}`] = 'low confidence';
                  }
                });
                if (item.invoiceItemTaxes) {
                  item.invoiceItemTaxes.forEach((itemTax, taxIndex) => {
                    const relevantKeys = ['name'];
                    relevantKeys.forEach((key) => {
                      if (
                        !confidenceDictionary[
                          `invoiceItems[${index}].invoiceItemTaxes[${taxIndex}].${key}`
                        ]
                      ) {
                        newErrors[
                          `invoiceItems[${index + 1}].invoiceItemTaxes[${taxIndex + 1}].${key}`
                        ] = 'missing confidence';
                      } else if (
                        confidenceDictionary[
                          `invoiceItems[${index}].invoiceItemTaxes[${taxIndex}].${key}`
                        ] < 100
                      ) {
                        newErrors[
                          `invoiceItems[${index + 1}].invoiceItemTaxes[${taxIndex + 1}].${key}`
                        ] = 'low confidence';
                      }
                    });
                  });
                }
              });
            }

            if (responseData.invoiceTaxes) {
              responseData.invoiceTaxes.forEach((tax, index) => {
                const relevantKeys = ['description'];
                relevantKeys.forEach((key) => {
                  if (!confidenceDictionary[`invoiceTaxes[${index}].${key}`]) {
                    newErrors[`invoiceTaxes[${tax.idTaxRate}].${key}`] = 'missing confidence';
                  } else if (confidenceDictionary[`invoiceTaxes[${index}].${key}`] < 100) {
                    newErrors[`invoiceTaxes[${tax.idTaxRate}].${key}`] = 'low confidence';
                  }
                });
              });
            }

            setErrors(newErrors);
          }

          api.invoiceApi
            .apiVversionInvoiceFileGet('1', responseData.fileURI)
            .then((fileResponse) => {
              const fileData = fileResponse.data?.['data'] as any;
              setInput((prevInput) => ({
                ...prevInput,
                attachments: [fileData],
              }));
              api.invoiceApi
                .apiVversionPersonaPersonasByEntityTypesGet('1', [1, 3, 4])
                .then((response) => {
                  const responseData = response.data?.['data'];
                  setBillFromPersonas(
                    responseData.queryResult.filter(
                      (persona) => persona.idEntityType === 1 || persona.idEntityType === 3
                    )
                  );
                  setBillToPersonas(
                    responseData.queryResult.filter((persona) => persona.idEntityType === 4)
                  );
                });
            });
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      const loadInvoice = async () => {
        setIsManualCreation(false);
        setIsLoading(true);
        try {
          if (Number.isNaN(Number(id))) {
            return;
          }
          const response = await api.invoiceApi.apiVversionInvoiceIdGet(Number(id), '1');
          const responseData = response.data?.['data'] as any;
          responseData.invoiceItems = responseData.invoiceItems.map((item) => ({
            ...item,
            rate: item.amount,
            amount: item.amount * item.quantity,
            invoiceItemTaxes: (item.invoiceItemTaxes ?? []).map((tax) => ({
              id: tax.idTaxRate,
              rate: tax.taxRate,
              amount: tax.taxAmount,
            })),
          }));
          setInput((prevInput) => ({
            ...prevInput,
            ...responseData,
            idCurrency: responseData.currency.id,
            date: new Date(responseData.date),
          }));

          try {
            const fileResponse = await api.invoiceApi.apiVversionInvoiceFileGet('1', responseData.fileURI);
            const fileData = fileResponse.data?.['data'] as any;
            setInput((prevInput) => ({
              ...prevInput,
              attachments: [fileData],
            }));
          } catch (error) {
            setErrors({ ...errors, attachments: error.message });
          }
        } catch (error) {
          setErrors({ ...errors, invoiceItems: error.message });
        } finally {
          setIsLoading(false);
        }
      };
      if (!isCurrencyLoading && !isInvoiceTypeLoading) {
        loadInvoice();
      }
    }
  }, [review, isCurrencyLoading, isInvoiceTypeLoading]);

  useEffect(() => {
    if (input.idFrom) {
      setIsCountryLoading(true);
      api.invoiceApi
        .apiVversionPersonaIdGet(input.idFrom, '1')
        .then((response) => {
          const responseData = response.data?.['data'];
          setCountryId(responseData.idCountry);
        })
        .catch((error) => {
          setCountryId(null);
        })
        .finally(() => {
          setIsCountryLoading(false);
        });
    }
  }, [input.idFrom]);

  useEffect(() => {
    if (countyId && !isCountryLoading) {
      loadInvoiceType();
      loadTaxRates();
    }
  }, [countyId, isCountryLoading]);

  const loadBillingAddressByPersonaId = (id, type) => {
    if (type === 'to') {
      setIsBillingAddressToLoading(true);
    } else {
      setIsBillingAddressFromLoading(true);
    }
    api.addressApi
      .apiVversionAddressAddressByPersonaIdGet(id, '1')
      .then((response) => {
        const responseData = response.data?.['data'];
        const formattedAddress =
          locale === 'en'
            ? `${responseData.address1}, ${responseData.city}, ${responseData.stateProvince.code} ${responseData.zipCode}, ${responseData.country.name}`
            : `${responseData.address1}, ${responseData.city}, ${responseData.stateProvince.name}, ${responseData.country.name}, ${responseData.zipCode}`;
        if (!edit) {
          setInput((prev) => ({
            ...prev,
            [type === 'to' ? 'addressTo' : 'addressFrom']: formattedAddress,
          }));
        }
      })
      .catch((error) => {
        if (type === 'to') {
          setInput((prev) => ({
            ...prev,
            addressTo: '',
          }));
        } else {
          setInput((prev) => ({
            ...prev,
            addressFrom: '',
          }));
        }
      })
      .finally(() => {
        setIsBillingAddressToLoading(false);
        setIsBillingAddressFromLoading(false);
      });
  };

  const loadBillToPersonas = async () => {
    setIsBillToPersonasLoading(true);
    await api.invoiceApi
      .apiVversionPersonaEligibleInvoiceToGet('1')
      .then((response) => {
        const responseData = response.data?.['data'].queryResult;
        setBillToPersonas(responseData);
      })
      .catch((error) => {
        setBillToPersonas([]);
      })
      .finally(() => {
        setIsBillToPersonasLoading(false);
      });
  };

  const loadBillFromPersonas = async () => {
    setIsBillFromPersonasLoading(true);
    await api.invoiceApi
      .apiVversionPersonaEligibleInvoiceFromGet('1')
      .then((response) => {
        const responseData = response.data?.['data'];
        setBillFromPersonas([responseData]);
        setInput((prev) => ({
          ...prev,
          idFrom: responseData?.id,
          from: responseData?.name,
        }));
      })
      .catch((error) => {
        setBillFromPersonas([]);
      })
      .finally(() => {
        setIsBillFromPersonasLoading(false);
      });
  };

  const loadTaxRates = async () => {
    setIsLoading(true);
    await api.invoiceApi
      .apiVversionTaxRateCountryIdGet(countyId, '1')
      .then((response) => {
        const responseData: TaxRateSearchResponse = response.data?.['data'];
        setTaxesValues(responseData.queryResult);
      })
      .catch((error) => {
        setTaxesValues([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const loadInvoiceType = async () => {
    setIsInvoiceTypeLoading(true);
    await api.invoiceApi
      .apiVversionInvoiceTypeCountryIdCountryGet(countyId, '1')
      .then((response) => {
        const responseData = response.data?.['data'].queryResult;
        setInvoiceTypes(responseData);
      })
      .catch((error) => {
        setInvoiceTypes([]);
      })
      .finally(() => {
        setIsInvoiceTypeLoading(false);
      });
  };

  const loadCurrencies = async () => {
    setIsCurrencyLoading(true);
    await api.invoiceApi
      .apiVversionCurrencyAllGet('1')
      .then((response) => {
        setCurrencies(response.data?.data?.queryResult);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsCurrencyLoading(false);
      });
  };

  useEffect(() => {
    if (input.idFrom && input.idFrom !== '' && !review && !view) {
      loadBillingAddressByPersonaId(input.idFrom, 'from');
    }
  }, [input.idFrom]);

  useEffect(() => {
    if (input.idTo && input.idTo !== '' && !review && !view) {
      loadBillingAddressByPersonaId(input.idTo, 'to');
    }
  }, [input.idTo]);

  useEffect(() => {
    if (!view) {
      loadCurrencies();
      if (!review) {
        loadBillFromPersonas();
        loadBillToPersonas();
      }
    }
  }, []);

  return (
    <InvoiceContext.Provider
      value={{
        isManualCreation,
        taxesValues,
        setTaxesValues,
        invoiceTypes,
        isLoading,
        currencies,
        setCurrencies,
        billToPersonas,
        billFromPersonas,
        isBillFromPersonasLoading,
        input,
        setInput,
        isBillToPersonasLoading,
        isBillingAddressFromLoading,
        isBillingAddressToLoading,
        errors,
        setErrors,
      }}
    >
      {children}
    </InvoiceContext.Provider>
  );
};

export const useInvoice = () => {
  const context = useContext(InvoiceContext);
  if (!context) {
    throw new Error('useInvoice must be used within an InvoiceProvider');
  }
  return context;
};
